import { action, computed, makeObservable, observable } from "mobx"

import type { IEpochRepository } from "@/domains/epochs/interfaces"
import type { EpochOrchestrator } from "@/domains/epochs/epoch-orchestrator"
import type { ScaleStatsStore } from "@/domains/scale-stats/scale-stats-store"

import type { HeroEmissionsStore } from "./hero-emissions-store"

import type { HeroBlockStat } from "../types"

const COINS_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="M16 14h1v4"/></svg>'
const VOTE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 12 2 2 4-4"/><path d="M5 7c0-1.1.9-2 2-2h10a2 2 0 0 1 2 2v12H5V7Z"/></svg>'
const ZAP_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg>'
const LOCK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>'
const TRENDING_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>'

/**
 * Descriptor for a stat that derives its value from observables.
 * Use getValue/getSub when the stat should update when underlying data changes
 * (e.g. emissions API, epoch store, user's lock). The getters are called each time
 * blockStats is computed; they can read any MobX observable and MobX will track
 * the dependency.
 *
 * For static values, pass () => "value" or use the legacy value/sub fields.
 */
export interface InjectedHeroBlockStatDescriptor {
  readonly id: string
  readonly label: string
  /** Getter - called when blockStats is computed; read observables here for reactivity */
  readonly getValue: () => string
  /** Getter - called when blockStats is computed */
  readonly getSub: () => string
  readonly iconSvg?: string
}

/** @deprecated Use InjectedHeroBlockStatDescriptor with getters for reactive values */
export interface InjectedHeroBlockStatInput {
  readonly id: string
  readonly label: string
  readonly value: string
  readonly sub: string
  readonly iconSvg?: string
}

const DEFAULT_ICON = COINS_SVG

/** Placeholder values shown before Stats/Epoch APIs return. Ensures the initial 3 blocks have data. */
const PLACEHOLDER_STATS = {
  epochIncentives: "Loading…",
  incentivesPerVote: "Loading…",
  bribeROI: "Loading…",
  tvl: "Loading…",
  emissionsRate: "Loading…",
} as const

export class HeroBlockStatsStore {
  private readonly statsStore: ScaleStatsStore
  private readonly epochRepository: IEpochRepository
  private readonly epochOrchestrator: EpochOrchestrator | undefined
  private readonly heroEmissionsStore: HeroEmissionsStore | undefined
  private readonly chainSlug: string | undefined

  /** Stored descriptors; getters invoked when blockStats is computed */
  injectedStatDescriptors: InjectedHeroBlockStatDescriptor[] = []

  constructor(
    statsStore: ScaleStatsStore,
    epochRepository: IEpochRepository,
    epochOrchestrator?: EpochOrchestrator,
    heroEmissionsStore?: HeroEmissionsStore,
    chainSlug?: string,
  ) {
    this.statsStore = statsStore
    this.epochRepository = epochRepository
    this.epochOrchestrator = epochOrchestrator
    this.heroEmissionsStore = heroEmissionsStore
    this.chainSlug = chainSlug

    makeObservable(this, {
      blockStats: computed,
      injectedStatDescriptors: observable,
      addInjectedStat: action,
      removeInjectedStat: action,
      clearInjectedStats: action,
    })
  }

  /**
   * Core + injected stats combined. Recomputes when Stats, Epoch, or any observable
   * read by injected stat getters changes.
   */
  get blockStats(): HeroBlockStat[] {
    const core = this.buildCoreStats()
    const injected = this.injectedStatDescriptors.map((d) => ({
      id: d.id,
      label: d.label,
      value: d.getValue(),
      sub: d.getSub(),
      iconSvg: (d.iconSvg ?? DEFAULT_ICON) as string,
    }))
    return [...core, ...injected]
  }

  /**
   * Add a stat block (e.g. when user connects - their VE power, locked SCALE, etc.).
   * Use getValue/getSub so the stat updates when underlying observables change.
   * Replaces existing stat with same id if present.
   */
  addInjectedStat(input: InjectedHeroBlockStatDescriptor | InjectedHeroBlockStatInput): void {
    const descriptor =
      "getValue" in input
        ? input
        : {
            ...input,
            getValue: () => input.value,
            getSub: () => input.sub,
          }
    const existing = this.injectedStatDescriptors.findIndex((d) => d.id === descriptor.id)
    const entry: InjectedHeroBlockStatDescriptor = {
      id: descriptor.id,
      label: descriptor.label,
      getValue: descriptor.getValue,
      getSub: descriptor.getSub,
      iconSvg: descriptor.iconSvg,
    }
    if (existing >= 0) {
      this.injectedStatDescriptors[existing] = entry
    } else {
      this.injectedStatDescriptors = [...this.injectedStatDescriptors, entry]
    }
  }

  /**
   * Remove an injected stat by id.
   */
  removeInjectedStat(id: string): void {
    this.injectedStatDescriptors = this.injectedStatDescriptors.filter((d) => d.id !== id)
  }

  /**
   * Clear all injected stats.
   */
  clearInjectedStats(): void {
    this.injectedStatDescriptors = []
  }

  private buildCoreStats(): HeroBlockStat[] {
    const stats = this.statsStore
    const currentEpoch = this.epochRepository.currentEpoch
    const epochQueryResult = this.epochOrchestrator?.currentEpochQuery?.lastResult

    const hasStatsData = stats.totalIncentives > 0 || stats.totalTvl > 0
    const hasPrice = stats.price > 0
    const gaugesMint = this.resolveGaugesMint(currentEpoch, epochQueryResult)
    const canShowEpochMetrics = hasStatsData && hasPrice && gaugesMint != null

    const epochIncentives = hasStatsData ? stats.displayTotalIncentives() : PLACEHOLDER_STATS.epochIncentives
    const incentivesPerVote = hasStatsData ? stats.displayIncentivesPerVote() : PLACEHOLDER_STATS.incentivesPerVote
    const tvl = hasStatsData ? stats.displayTotalTvl() : PLACEHOLDER_STATS.tvl

    let bribeROIValue: string
    let emissionsRateValue: string

    if (canShowEpochMetrics) {
      const bribeROI = stats.calculateBribeROI(gaugesMint)
      bribeROIValue = `${bribeROI.toFixed(1)}X`

      const emissionsPerDay = gaugesMint / 7
      emissionsRateValue = Math.round(emissionsPerDay).toLocaleString("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })
    } else {
      bribeROIValue = PLACEHOLDER_STATS.bribeROI
      emissionsRateValue = PLACEHOLDER_STATS.emissionsRate
    }

    return [
      { id: "epoch-incentives", label: "Epoch incentives", value: epochIncentives, sub: "", iconSvg: COINS_SVG },
      { id: "incentives-per-vote", label: "Incentives per vote", value: incentivesPerVote, sub: "", iconSvg: VOTE_SVG },
      { id: "bribe-roi", label: "Bribe ROI", value: bribeROIValue, sub: "", iconSvg: ZAP_SVG },
      { id: "tvl", label: "TVL", value: `$${tvl}`, sub: "", iconSvg: LOCK_SVG },
      {
        id: "emissions-rate",
        label: "Emissions rate",
        value: emissionsRateValue,
        sub: "SCALE / day",
        iconSvg: TRENDING_SVG,
      },
    ]
  }

  private resolveGaugesMint(
    currentEpoch: IEpochRepository["currentEpoch"],
    epochQueryResult: { gaugesMint?: string } | null | undefined,
  ): number | null {
    if (currentEpoch != null) {
      const mint = parseFloat(currentEpoch.gaugesMint)
      return Number.isFinite(mint) ? mint : 0
    }

    if (epochQueryResult?.gaugesMint != null) {
      const mint = parseFloat(epochQueryResult.gaugesMint)
      return Number.isFinite(mint) ? mint : 0
    }

    if (this.heroEmissionsStore != null && this.chainSlug != null) {
      return this.heroEmissionsStore.getEpochGaugesMint(this.chainSlug)
    }

    return null
  }
}
